import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';

import cognitoConfig from './cognito_config.json';
import infrastructureOutputs from './infrastructure_outputs.json';

/**
 * Configures AWS Amplify with Cognito authentication and secure cookie storage
 */
export const configureAmplify = () => {
  const currentDomain = window.location.hostname;
  let rootDomain = currentDomain;

  // Determine the root domain for cookie sharing
  if (currentDomain === 'localhost') {
    rootDomain = 'localhost';
  } else {
    rootDomain = `.${cognitoConfig.aws_cognito_domain_name}`;
  }

  // Create secure cookie storage configuration
  const cookieStorage = new CookieStorage({
    domain: rootDomain,
    path: '/',
    secure: window.location.protocol === 'https:',
    expires: 365, // days
    sameSite: 'lax' // Allow cookies to be sent in cross-site requests
  });

  // Get OAuth domain from infrastructure outputs (preferred) or environment variables (fallback)
  let oauthDomain = (infrastructureOutputs as any).CognitoOAuthDomainUrl || process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
  
  // Clean the domain - remove protocol if accidentally included
  if (oauthDomain) {
    oauthDomain = oauthDomain.replace(/^https?:\/\//, '');
  }
  
  // Base Amplify configuration
  const authConfig: any = {
    userPoolId: cognitoConfig.aws_user_pools_id,
    userPoolClientId: cognitoConfig.aws_user_pools_web_client_id,
    signUpVerificationMethod: 'code',
    loginWith: {
      username: false,
      email: true,
      phone: false
    }
  };

  // Add OAuth configuration only if domain is provided
  if (oauthDomain) {
    authConfig.loginWith.oauth = {
      domain: oauthDomain,
      scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
      redirectSignIn: [window.location.origin + '/'],
      redirectSignOut: [window.location.origin + '/'],
      responseType: 'code'
    };
    console.log('OAuth configuration enabled with domain:', oauthDomain);
  } else {
    console.log('OAuth not configured - CognitoOAuthDomainUrl not found in infrastructure outputs and REACT_APP_COGNITO_OAUTH_DOMAIN not set');
  }

  // Configure Amplify
  Amplify.configure({
    Auth: {
      Cognito: authConfig
    }
  });

  // Configure token storage to use secure cookies
  cognitoUserPoolsTokenProvider.setKeyValueStorage(cookieStorage);

  console.log('Amplify configured with secure cookie storage for domain:', rootDomain);
}

/**
 * Get current authentication configuration
 */
export const getAmplifyConfig = () => {
  return {
    userPoolId: cognitoConfig.aws_user_pools_id,
    userPoolClientId: cognitoConfig.aws_user_pools_web_client_id,
    region: cognitoConfig.aws_cognito_region,
  };
}

/**
 * Check if Google OAuth is configured and available
 */
export const isGoogleOAuthAvailable = (): boolean => {
  const oauthDomain = (infrastructureOutputs as any).CognitoOAuthDomainUrl || process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
  return !!oauthDomain;
}

/**
 * Get the OAuth domain URL for debugging/display purposes
 */
export const getOAuthDomain = (): string | null => {
  const domain = (infrastructureOutputs as any).CognitoOAuthDomainUrl || process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
  return domain ? domain.replace(/^https?:\/\//, '') : null;
}